import { formatUnconvertedDateCustom } from "@app/shared";
import { wafflesKeyColumnName } from "@app/shared/waffles";
import { AccuracyInspectionSupplement } from "@app/projects/results/accuracy";
import { keyBy, merge, sortBy, values } from "lodash";
import generateDefaultProviderPanelRow from "./defaultProviderPanelRow";
import InspectionProfileTableData from "./inspectionProfileTableData";
import { getSupplementalDataDecoration, valueOrDashes } from "./utils";
import { AccuracySupplementalTypeEnum } from "../../../enumerators";

const unknownSourceName = "UNKNOWN";
const credentialRowKeys: string[] = [];

// have to disable sort-keys here until we can change how the table data is generated on the panel.

/* eslint-disable sort-keys */
export const generateCredentialProviderPanelRows = (rowKey: string, title: string, sourceValue: string, supplements: AccuracyInspectionSupplement<string>[]): InspectionProfileTableData[] => {
  const tableData: InspectionProfileTableData[] = [];

  if (((sourceValue && sourceValue.length > 0) || (supplements && supplements.length > 0))) {
    joinCredentials(sourceValue, supplements).forEach((credential, index) => {
      const currentRowTitle = index === 0 ? title : "";
      const currentRowKey = `${rowKey}-${index}`;
      credentialRowKeys.push(currentRowKey);

      tableData.push({
        [wafflesKeyColumnName]: currentRowKey,
        title: currentRowTitle,
        source: (credential.providerValue ?
          <div key={index}>
            <div>
              {valueOrDashes(credential.providerValue)}
            </div>
          </div> :
          ""),
        result: (
          <div key={index}>
            {getSupplementalDataDecoration(
              (<span>{valueOrDashes(credential.attestationValue)}</span>),
              credential.supplementalType,
              null,
            )}
          </div>
        ),
        reference: (
          (supplements && supplements[0]?.sourceName.toUpperCase() !== unknownSourceName) ?
            <span>
              {valueOrDashes(credential.sourceName)}
              &nbsp;
              {(credential.sourceName) ?
                valueOrDashes(credential.sourceDate ? `(${formatUnconvertedDateCustom(credential.sourceDate, "L")})` : "") :
                null
              }
            </span> :
            <span>{"---"}</span>)
      });
    });
  } else {
    tableData.push(
      generateDefaultProviderPanelRow(rowKey, title, null, null)
    );
  }

  return tableData;
}

const normalizeCredential = (value: string) : string => {
  const regex = /[^a-zA-Z]/gm;
  return value.toLowerCase().replace(regex, '');
}

export const getCredentialRowKeys = () : string[] => credentialRowKeys;

interface SortedCredential {
  attestationValue: string,
  comparedValue: string,
  providerValue: string,
  sourceDate: Date,
  sourceName: string,
  supplementalType: AccuracySupplementalTypeEnum
}

const joinCredentials = (sourceValue: string, supplements: AccuracyInspectionSupplement<string>[]) : SortedCredential[] => {
  let creds = sourceValue.split(/[;,]+/).map((cred) =>
  ({
    comparedValue: normalizeCredential(cred),
    providerValue: cred
  }));

  const supps = supplements.filter((obj) => obj.supplementalType != "unknown").map((cred) =>
  ({
    attestationValue: cred.value,
    comparedValue: normalizeCredential(cred.value),
    sourceDate: cred.sourceDate,
    sourceName: cred.sourceName,
    supplementalType: cred.supplementalType
  }));

  if (supps.length > 0)
    creds = creds.filter(cred => cred.providerValue != '');

  const merged = merge(keyBy(creds, "comparedValue"), keyBy(supps, "comparedValue"));
  const sorted = sortBy(merged, [((cred) => cred && cred.providerValue ? cred.providerValue : null), ((cred) => cred && cred.attestationValue ? cred.attestationValue : null)]);
  return values(sorted);
}
